import { useMemo } from "react";
import { useAssertedTenancyContext } from "@bfc-software/app-toolbox";
import type { OrgCfgSchemaTypes } from "@bfc-software/api-schemas";
import { useTaskPlannerApi } from "@bfc-software/bfc-web-ui/lib/task-planner-api-client";
import { useOrgCfgApi } from "@bfc-software/bfc-web-ui/lib/orgcfg-api-client";

import { useSelectorRoster, type SelectorOption } from "./use-selector-roster";
import type { QueryLike } from "@bfc-software/bfc-web-ui/lib/async-utils";
import { selectorGroupOf, type SelectorGroup } from "./eligibility";

type AppUserGroup = OrgCfgSchemaTypes.components["schemas"]["appUserGroups"];

// Same scope as `use-selector-roster.ts`'s `APP_NAME` and `user-groups.tsx`'s.
const APP_NAME = "select";

/** One dropdown option. `id` is the planner `selector_profile.id` ULID - the value `selector_id` takes. */
export interface AssignableSelector {
  id: string;
  label: string;
  /** The user groups the selector belongs to; empty when ungrouped. Eligibility compares ids, the dropdown draws names. */
  groups: ReadonlyArray<SelectorGroup>;
}

/**
 * Pure join, exported for its own test. Keeps a planner selector only if the AAA roster
 * (active users only, `useSelectorRoster`) has them; labels from the roster; groups from
 * `group.users` containing the selector's `external_user_id`, kept as `SelectorGroup`s (ids and
 * names). Sorted by label.
 */
export function joinAssignableSelectors(
  selectors: ReadonlyArray<{ id: string; external_user_id: string }>,
  roster: ReadonlyArray<SelectorOption>,
  groups: ReadonlyArray<Pick<AppUserGroup, "id" | "name" | "users">>,
): AssignableSelector[] {
  return selectors
    .flatMap((selector) => {
      const person = roster.find((o) => o.id === selector.external_user_id);
      if (!person) return [];
      return [
        {
          id: selector.id,
          label: person.label,
          groups: groups
            .filter((group) => group.users.includes(selector.external_user_id))
            .map(selectorGroupOf),
        },
      ];
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The manual-assignment dropdown's options: planner selectors (the ids `selector_id` takes),
 * named from the AAA roster and labelled with their user groups from orgcfg.
 *
 * Three reads joined by hand, as `useSelectorDirectory` joins two. Eligibility is *not* applied
 * here (it is per task selection); `eligibleSelectors` in `eligibility.ts` does that.
 * A failed groups read fails the whole hook: the dropdown is a hard join.
 */
export function useAssignableSelectors(): QueryLike<AssignableSelector[]> {
  const { orgId, warehouseId } = useAssertedTenancyContext();
  const client = useTaskPlannerApi();
  const orgCfgClient = useOrgCfgApi();

  // Same key as `useSelectorDirectory`, so cache-warm.
  const selectors = client.useQuery(
    "get",
    "/{orgId}/selectors",
    {
      params: {
        path: { orgId },
        query: { warehouseExtId: warehouseId, limit: 100 },
      },
    },
    { select: (data) => data.selectors },
  );
  const roster = useSelectorRoster();
  const groups = orgCfgClient.useQuery("get", "/orgs/user_groups/list", {
    params: {
      query: {
        orgId,
        warehouseId,
        appName: APP_NAME,
      },
    },
  });

  const data = useMemo(
    () =>
      selectors.data && roster.data && groups.data
        ? joinAssignableSelectors(selectors.data, roster.data, groups.data)
        : undefined,
    [selectors.data, roster.data, groups.data],
  );

  return {
    isPending: selectors.isPending || roster.isPending || groups.isPending,
    isError: selectors.isError || roster.isError || groups.isError,
    error: selectors.error ?? roster.error ?? groups.error,
    data,
  };
}
